import actions from "./actions";
import ZonesStateRecord from "@infinode/records/reducers/ZonesStateRecord";
import { createZoneRecord } from "@infinode/records/ZoneRecord";
import { ZONE_TYPES } from "src/infinode/constants/zone";
import sitesActions from "../sites/actions";
import { Set } from "immutable";
import reducerUtils from "../reducerUtils";
import { SETTINGS_DATA_SOURCE_TYPE_PROP_NAMES } from "@infinode/components/settings/constants";
import DataSourceValueRecord from "@infinode/records/DataSourceValueRecord";
import {List} from "immutable";

const updateZonesMap = (zones, newZones, siteId) => {
  return zones.withMutations(zonesMutator => {
    newZones.forEach(z => {
      const zoneRecord = createZoneRecord(z, siteId);
      zonesMutator.set(z.id, zoneRecord);
    });
  });
};

const updateZonesListBySite = (zonesBySiteMap, newZones, siteId) => {
  return zonesBySiteMap.set(siteId, Set(newZones.map(z => z.id)));
};

const getZones = (state, newZones, siteId) => {
  return state.merge({
    loading: false,
    zones: updateZonesMap(state.zones, newZones, siteId),
    zoneIdsBySiteId: updateZonesListBySite(state.zoneIdsBySiteId, newZones, siteId),
    selectedZoneId: ""
  });
};

const updateZoneBySite = (zonesBySiteMap, zone, siteId) => {
  const zoneIdsList = zonesBySiteMap.get(siteId) || Set();
  return zonesBySiteMap.set(siteId, zoneIdsList.add(zone.id));
};

const updateZoneMap = (zones, newZone, siteId) => {
  return zones.set(newZone.id, createZoneRecord(newZone, siteId));
};

const getZone = (state, newZone, siteId) => {
  return state.merge({
    loading: false,
    zones: updateZoneMap(state.zones, newZone, siteId),
    zoneIdsBySiteId: updateZoneBySite(state.zoneIdsBySiteId, newZone, siteId)
  });
};

const deleteZoneIdFromSite = (zoneIdsBySiteId, zoneId, siteId) => {
  return zoneIdsBySiteId.update(siteId, set => (set ? set.delete(zoneId) : set));
};

/**
 * Make sure only one main zone or end zone when a zone's typing is changing
 * i.e. if this zone is changing to main or end, the others cannot be main or end
 * @param {Map} zones
 * @param {string} zoneId The zone of changing
 * @param {*} zoneType The zoneType of changing
 * @param {*} siteId the site id context
 * @returns
 */
const checkZoneType = (zones, zoneId, zoneType, siteId) => {
  let newZones = zones;
  if (zoneType === ZONE_TYPES.MAIN || zoneType === ZONE_TYPES.ENDORPROSUMER) {
    newZones = newZones.map(z =>
      z.id === zoneId || z.siteId !== siteId || z.zoneType !== zoneType ? z : z.set("zoneType", ZONE_TYPES.NEITHERORCONSUMER)
    );
  }
  return newZones;
};

const updateZoneType = (zones, zoneId, zoneType, siteId) => {
  let newZones = zones.setIn([zoneId, "zoneType"], zoneType);
  newZones = checkZoneType(newZones, zoneId, zoneType, siteId);
  return newZones;
};

/**
 * This will re-create new zone record
 * @param {Map} zones
 * @param {object} newZone
 * @param {string} siteId
 * @returns
 */
const updateZoneWithTypeCheck = (zones, newZone, siteId) => {
  let newZones = updateZoneMap(zones, newZone, siteId);
  newZones = checkZoneType(newZones, newZone.id, newZone.zoneType, siteId);
  return newZones;
};

const setSelectedZoneId = (state, action) => {
  let zoneId = "";
  const { zoneIdsBySiteId } = state;
  const zoneIds = zoneIdsBySiteId.get(action.siteId);
  if (zoneIds && zoneIds.find(zId => zId === action.zoneId)) {
    zoneId = action.zoneId;
  }
  return state.set("selectedZoneId", zoneId);
};

export default function zonesReducer(state = ZonesStateRecord(), action) {
  const { zones, zone, siteId } = action;
  switch (action.type) {
    case actions.GET_ZONES_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.GET_ZONES_SUCCESS:
      return getZones(state, zones, siteId);
    case actions.GET_ZONE_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.GET_ZONE_SUCCESS:
      return getZone(state, zone, siteId);
    case actions.DELETE_ZONE_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.DELETE_ZONE_SUCCESS:
      // TODO: what if delete main/end zone?
      return state.merge({
        loading: false,
        zones: state.zones.delete(action.id),
        zoneIdsBySiteId: deleteZoneIdFromSite(state.zoneIdsBySiteId, action.id, siteId)
      });
    case actions.UPDATE_ZONE_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.UPDATE_ZONE_SUCCESS:
      return state.merge({
        loading: false,
        zones: updateZoneWithTypeCheck(state.zones, zone, siteId)
      });
    case actions.UPDATE_ZONE_TYPE_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.UPDATE_ZONE_TYPE_SUCCESS:
      return state.merge({
        loading: false,
        zones: updateZoneType(state.zones, zone.id, zone.zoneType, siteId)
      });
    case actions.CREATE_ZONE_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.CREATE_ZONE_SUCCESS:
      return state.merge({
        loading: false,
        zones: updateZoneWithTypeCheck(state.zones, zone, siteId),
        zoneIdsBySiteId: updateZoneBySite(state.zoneIdsBySiteId, zone, siteId)
      });
    case actions.ZONES_ERROR:
      return state.merge({ loading: false, error: action.error });
    case actions.SET_SELECTED_ZONE_ID:
      return setSelectedZoneId(state, action);
    case actions.ADD_ZONE_DATA_SOURCE_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.ADD_ZONE_DATA_SOURCE_SUCCESS:
      return state.withMutations(m => {
        m.set("loading", false);
        action.circuitDataSourceList.forEach(ds => {
          m.updateIn(
            ["zones", action.zoneId, "dataSource", SETTINGS_DATA_SOURCE_TYPE_PROP_NAMES[ds.dataSourceType]],
            values => {
              const newValue = DataSourceValueRecord({ entityId: ds.circuitId, positive: ds.positive });
              const foundIndex = values.findIndex(v => v.entityId === ds.circuitId);
              return foundIndex >= 0 ? values.set(foundIndex, newValue) : values.push(newValue);
            }
          );
        });
      });
    case actions.REMOVE_ZONE_DATA_SOURCE_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.REMOVE_ZONE_DATA_SOURCE_SUCCESS:
      return state.withMutations(m => {
        m.set("loading", false);
        action.circuitDataSourceList.forEach(ds => {
          m.updateIn(
            ["zones", action.zoneId, "dataSource", SETTINGS_DATA_SOURCE_TYPE_PROP_NAMES[ds.dataSourceType]],
            values => values.filter(v => v.entityId !== ds.circuitId)
          );
        });
      });
    case actions.UPDATE_ZONE_DEMAND_CONFIG_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.UPDATE_ZONE_DEMAND_CONFIG_SUCCESS:
      return state.withMutations(s => {
        s.merge({ loading: false, error: null });
        s.updateIn(["zones", action.zoneId], s => s.merge({ demandOn: action.demandSwitch }));
      });   
    case actions.UPDATE_ZONE_DLC_CONFIG_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.UPDATE_ZONE_DLC_CONFIG_SUCCESS:
      return state.withMutations(s => {
        s.merge({ loading: false, error: null });
        s.updateIn(["zones", action.zoneId], s => s.merge({ DLCId: action.DLCId }));
      });
    case actions.UPDATE_ZONE_METERS_CONFIG_REQUEST:
      return reducerUtils.getRequestState(state);
    case actions.UPDATE_ZONE_METERS_CONFIG_SUCCESS:
      return state.withMutations(s => {
        s.merge({ loading: false, error: null });
        s.updateIn(["zones", action.zoneId, 'meters'], meters => {
          const meterList = List.isList(meters) ? meters : List(meters||[]);
          meters.includes(action.toggledMeterId)
          ?meters.filter(id=>id !== action.toggledMeterId)//remove
          :meters.push(action.toggledMeterId) //add
          });
      });    
    // Site action
    case sitesActions.DELETE_SITE_SUCCESS:
      return state.update("zoneIdsBySiteId", map => map.set(action.id, Set()));
    case sitesActions.SET_SELECTED_SITE_ID:
      return state.set("selectedZoneId", "");

    default:
      return state;
  }
}
